import { z } from 'zod';

function isTemplateValueTypeMatch(
  valueType: QueryTemplateValueType,
  value: QueryTemplateInputValue
): boolean {
  switch (valueType) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number';
    case 'boolean':
      return typeof value === 'boolean';
    case 'null':
      return value === null;
    default:
      return false;
  }
}

export const semanticQueryModeSchema = z.enum(['single_asset', 'join', 'multi_query_merge']);
export type SemanticQueryMode = z.infer<typeof semanticQueryModeSchema>;

export const querySelectRoleSchema = z.enum(['metric', 'field']);
export type QuerySelectRole = z.infer<typeof querySelectRoleSchema>;

export const queryFilterOperatorSchema = z.enum([
  'equals',
  'not_equals',
  'in',
  'not_in',
  'contains',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'set',
  'not_set',
]);
export type QueryFilterOperator = z.infer<typeof queryFilterOperatorSchema>;

export const querySelectItemSchema = z.object({
  memberId: z.string().min(1),
  role: querySelectRoleSchema,
  alias: z.string().optional(),
});
export type QuerySelectItem = z.infer<typeof querySelectItemSchema>;

export const queryFilterSchema = z.object({
  memberId: z.string().min(1),
  operator: queryFilterOperatorSchema,
  value: z.unknown().optional(),
});
export type QueryFilter = z.infer<typeof queryFilterSchema>;

export const queryTimeGranularitySchema = z.enum([
  'day',
  'week',
  'month',
  'quarter',
  'year',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
]);
export type QueryTimeGranularity = z.infer<typeof queryTimeGranularitySchema>;

export const queryRelativePeriodSchema = z.enum([
  '1w',
  '4w',
  '13w',
  '26w',
  '52w',
  '1m',
  '3m',
  '6m',
  '12m',
  'qtr',
  'qtd',
  'ytd',
  'mtd',
]);
export type QueryRelativePeriod = z.infer<typeof queryRelativePeriodSchema>;

export const queryTimeClauseSchema = z.object({
  memberId: z.string().min(1).optional(),
  granularity: queryTimeGranularitySchema.optional(),
  dateRange: z.union([z.string(), z.tuple([z.string(), z.string()])]).optional(),
  relativePeriod: queryRelativePeriodSchema.optional(),
});
export type QueryTimeClause = z.infer<typeof queryTimeClauseSchema>;

export const querySortDirectionSchema = z.enum(['asc', 'desc']);
export type QuerySortDirection = z.infer<typeof querySortDirectionSchema>;

export const querySortSchema = z.object({
  memberId: z.string().min(1),
  direction: querySortDirectionSchema,
});
export type QuerySort = z.infer<typeof querySortSchema>;

export const queryJoinTypeSchema = z.enum(['one_to_one', 'one_to_many', 'many_to_one', 'many_to_many']);
export type QueryJoinType = z.infer<typeof queryJoinTypeSchema>;

export const queryJoinEdgeSchema = z.object({
  sourceAssetId: z.string().min(1),
  targetAssetId: z.string().min(1),
  relationshipId: z.string().optional(),
  joinType: queryJoinTypeSchema.optional(),
});
export type QueryJoinEdge = z.infer<typeof queryJoinEdgeSchema>;

export const queryJoinStrategySchema = z.enum(['native_join', 'separate_then_merge']);
export type QueryJoinStrategy = z.infer<typeof queryJoinStrategySchema>;

export const queryJoinPlanSchema = z.object({
  path: z.array(queryJoinEdgeSchema),
  strategy: queryJoinStrategySchema,
});
export type QueryJoinPlan = z.infer<typeof queryJoinPlanSchema>;

export const queryContextSchema = z.object({
  timezone: z.string().optional(),
  locale: z.string().optional(),
  tenantId: z.string().optional(),
});
export type QueryContext = z.infer<typeof queryContextSchema>;

export const semanticQueryDraftSchema = z.looseObject({
  connectionId: z.string().nullable(),
  mode: semanticQueryModeSchema,
  rootAssetId: z.string().nullable(),
  assetIds: z.array(z.string()),
  select: z.array(querySelectItemSchema),
  filters: z.array(queryFilterSchema),
  groupBy: z.array(z.string()),
  time: queryTimeClauseSchema.optional(),
  sort: z.array(querySortSchema),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().min(0).optional(),
  parameters: z.record(z.string(), z.unknown()).default({}),
  joinPlan: queryJoinPlanSchema.optional(),
  context: queryContextSchema.optional(),
});
export type SemanticQueryDraft = z.infer<typeof semanticQueryDraftSchema>;

export const dataConnectionQueryTypeSchema = z.enum(['semantic_query_object', 'sql_template']);
export type DataConnectionQueryType = z.infer<typeof dataConnectionQueryTypeSchema>;

export const queryTemplateValueTypeSchema = z.enum(['string', 'number', 'boolean', 'null']);
export type QueryTemplateValueType = z.infer<typeof queryTemplateValueTypeSchema>;

export const queryTemplateInputValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type QueryTemplateInputValue = z.infer<typeof queryTemplateInputValueSchema>;

export const queryTemplatePredefinedVariableSchema = z.enum(['$IS_NATION']);
export type QueryTemplatePredefinedVariable = z.infer<typeof queryTemplatePredefinedVariableSchema>;

export const queryInputVariableDefinitionSchema = z.object({
  name: z.string().trim().min(1),
  type: queryTemplateValueTypeSchema,
  mappedVariable: queryTemplatePredefinedVariableSchema.optional(),
  description: z.string().trim().min(1).optional(),
});
export type QueryInputVariableDefinition = z.infer<typeof queryInputVariableDefinitionSchema>;

export const inputVariableDefinitionsSchema = z
  .record(z.string().trim().min(1), queryInputVariableDefinitionSchema)
  .superRefine((definitions, ctx) => {
    for (const [definitionKey, definition] of Object.entries(definitions)) {
      if (definition.name !== definitionKey) {
        ctx.addIssue({
          code: 'custom',
          message: `Variable key "${definitionKey}" must match definition name`,
          path: [definitionKey, 'name'],
        });
      }
    }
  });
export type InputVariableDefinitions = z.infer<typeof inputVariableDefinitionsSchema>;

export const postgresTemplateQueryDraftSchema = z.object({
  templateSql: z.string().trim().min(1),
});
export type PostgresTemplateQueryDraft = z.infer<typeof postgresTemplateQueryDraftSchema>;

export const semanticDataConnectionQueryPayloadSchema = z.object({
  queryType: z.literal('semantic_query_object'),
  queryJson: semanticQueryDraftSchema,
});

export const postgresDataConnectionQueryPayloadSchema = z.object({
  queryType: z.literal('sql_template'),
  queryJson: postgresTemplateQueryDraftSchema,
  inputVariableDefinitions: inputVariableDefinitionsSchema,
});

export const dataConnectionQueryPayloadSchema = z.discriminatedUnion('queryType', [
  semanticDataConnectionQueryPayloadSchema,
  postgresDataConnectionQueryPayloadSchema,
]);
export type DataConnectionQueryPayload = z.infer<typeof dataConnectionQueryPayloadSchema>;

export const dataConnectionQuerySchema = z
  .object({
    id: z.uuid(),
    dataConnectionId: z.uuid(),
    name: z.string(),
    description: z.string(),
    isDeleted: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    createdBy: z.string(),
    updatedBy: z.string().nullable(),
  })
  .and(dataConnectionQueryPayloadSchema);
export type DataConnectionQuery = z.infer<typeof dataConnectionQuerySchema>;

export const listDataConnectionQueriesResponseSchema = z.object({
  items: z.array(dataConnectionQuerySchema),
});
export type ListDataConnectionQueriesResponse = z.infer<typeof listDataConnectionQueriesResponseSchema>;

export const createDataConnectionQueryBodySchema = z
  .object({
    name: z.string().trim().min(1),
    description: z.string().optional(),
  })
  .and(dataConnectionQueryPayloadSchema);
export type CreateDataConnectionQueryBody = z.infer<typeof createDataConnectionQueryBodySchema>;

export const updateDataConnectionQueryBodySchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    description: z.string().optional(),
    queryType: dataConnectionQueryTypeSchema.optional(),
    queryJson: z.union([semanticQueryDraftSchema, postgresTemplateQueryDraftSchema]).optional(),
    inputVariableDefinitions: inputVariableDefinitionsSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.name === undefined &&
      value.description === undefined &&
      value.queryType === undefined &&
      value.queryJson === undefined &&
      value.inputVariableDefinitions === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one field must be provided',
      });
    }
  });
export type UpdateDataConnectionQueryBody = z.infer<typeof updateDataConnectionQueryBodySchema>;

export const queryTemplatePreviewInputsSchema = z.record(
  z.string().trim().min(1),
  queryTemplateInputValueSchema
);
export type QueryTemplatePreviewInputs = z.infer<typeof queryTemplatePreviewInputsSchema>;

export const previewDataConnectionSemanticQueryBodySchema = z.object({
  queryType: z.literal('semantic_query_object'),
  queryJson: semanticQueryDraftSchema,
});

export const previewDataConnectionPostgresQueryBodySchema = z
  .object({
    queryType: z.literal('sql_template'),
    queryJson: postgresTemplateQueryDraftSchema,
    inputVariableDefinitions: inputVariableDefinitionsSchema,
    previewInputs: queryTemplatePreviewInputsSchema.default({}),
  })
  .superRefine((value, ctx) => {
    const definitions = value.inputVariableDefinitions;
    const inputs = value.previewInputs;

    for (const [variableKey, inputValue] of Object.entries(inputs)) {
      const definition = definitions[variableKey];
      if (!definition) {
        ctx.addIssue({
          code: 'custom',
          message: `Unexpected preview input variable "${variableKey}"`,
          path: ['previewInputs', variableKey],
        });
        continue;
      }

      if (!isTemplateValueTypeMatch(definition.type, inputValue)) {
        ctx.addIssue({
          code: 'custom',
          message: `Preview input "${variableKey}" must be of type ${definition.type}`,
          path: ['previewInputs', variableKey],
        });
      }
    }

    for (const [variableKey] of Object.entries(definitions)) {
      const hasProvidedInput = inputs[variableKey] !== undefined;

      if (!hasProvidedInput) {
        ctx.addIssue({
          code: 'custom',
          message: `Missing required preview input variable "${variableKey}"`,
          path: ['previewInputs', variableKey],
        });
      }
    }
  });

export const previewDataConnectionQueryBodySchema = z.discriminatedUnion('queryType', [
  previewDataConnectionSemanticQueryBodySchema,
  previewDataConnectionPostgresQueryBodySchema,
]);
export type PreviewDataConnectionQueryBody = z.infer<typeof previewDataConnectionQueryBodySchema>;

export const previewDataConnectionQueryResponseSchema = z.object({
  columns: z.array(z.string()),
  rows: z.array(z.record(z.string(), z.unknown())),
  rowCount: z.number().int().nonnegative(),
  warnings: z.array(z.string()),
});
export type PreviewDataConnectionQueryResponse = z.infer<typeof previewDataConnectionQueryResponseSchema>;
